/**
 * This component wraps the entire application and catches any errors that bubble up from inside, displaying an error
 * page that redirects users back to the application home page.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import AuthButton from 'src/components/SignIn/AuthButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import LogoContainer from 'src/components/SignIn/LogoContainer';
import { LoginContainerConfigs } from 'src/components/SignIn/SignInContainer';
import { MarketingIframe, MarketingSiteContainer } from 'src/routes/routeTable';

class ErrorBoundary extends Component {
  static propTypes = {
    app: PropTypes.object.isRequired,
    children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired
  };

  static defaultProps = { children: null };

  state = { hasError: false };

  /**
   * If the user hits the browser back button while on the error page, we need to refresh the application manually
   * in order to purge the error state.
   */
  componentWillReceiveProps({ history: { action, goBack }, location: { pathname, search } }) {
    // We only want to do this when they click the browser back button while the application is in the error state.
    if (action !== 'POP' || !this.state.hasError) {
      return;
    }

    // Sometimes, the same route gets pushed multiple times.  In that case, we want to go back another step.
    if (this.props.location.pathname === pathname && this.props.location.search === search) {
      goBack();
    }
  }

  componentDidCatch(error, info) {
    const shouldReportError = !window.location.href.includes('-localhost');
    if (shouldReportError) {
      Sentry.captureException(error, {
        tags: {
          errorBoundary: true
        },
        extra: {
          url: window.location.href,
          componentStack: info?.componentStack
        }
      });
    }

    this.setState({ hasError: true });
    console.warn(error, info);
  }

  render() {
    const { app } = this.props;
    const backgroundColor = LoginContainerConfigs?.[app.name]?.bgColor;

    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', height: '100%' }}>
          <MarketingSiteContainer className="signin_marketing">
            <MarketingIframe />
          </MarketingSiteContainer>

          <div className="beacon-signin-form-container" style={{ backgroundColor }}>
            <div className="error-form">
              <LogoContainer title={app.displayName} />

              <Text
                sx={{
                  fontWeight: 500,
                  marginTop: '16px',
                  textAlign: 'center',
                  fontFamily: 'Geist',
                  lineHeight: '24px',
                  letterSpacing: '0.08px'
                }}
              >
                Sorry that we encountered an error.
              </Text>
              <Text
                sx={{
                  fontWeight: 500,
                  textAlign: 'center',
                  fontFamily: 'Geist',
                  lineHeight: '24px',
                  letterSpacing: '0.08px'
                }}
              >
                Please try again shortly or contact
              </Text>

              <Text
                sx={{
                  fontWeight: 500,
                  margin: '0 0 40px 0',
                  textAlign: 'center',
                  fontFamily: 'Geist',
                  lineHeight: '24px',
                  letterSpacing: '0.08px'
                }}
              >
                support@stackline.com.
              </Text>

              <AuthButton
                onClick={() => {
                  this.props.history.push('/');
                  window.location.reload();
                }}
              >
                Back to home
              </AuthButton>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
